import type { Unsubscribe, WatchEvent } from '../collection';
import type { LiveSource, QuerySource, Sources } from './types';

/* The source the authed console runs on today.
 *
 * It is NOT a stub that returns empty arrays. It never emits, so every store
 * stays in `unconfigured` and every screen says "not connected" rather than
 * "no data" — which are different facts, and the second one would be a lie:
 * the router is serving traffic, this console simply cannot read it yet.
 *
 * Wiring the real lanes replaces this module and nothing else. */

const never: Unsubscribe = () => {};

function noWatch<T>(_on: (ev: WatchEvent<T>) => void): Unsubscribe {
  return never;
}

const NOT_WIRED = 'the control plane has no read route for this yet';

function unavailable<T>(): Promise<T> {
  return Promise.reject(new Error(NOT_WIRED));
}

export const emptyLive: LiveSource = {
  kind: 'empty',
  watchEstates: noWatch,
  watchDeployments: (_estateId, _on) => never,
  watchTokens: (_estateId, _on) => never,
  watchClientKeys: (_estateId, _on) => never,
  watchLedger: (_q, _on) => never,
  watchTenants: (_estateId, _on) => never,
  listPoolKeys: () => unavailable(),
  listTenantKeys: () => unavailable(),
  listMemories: () => unavailable(),
  memoryStats: () => unavailable(),
  memoryTenants: () => unavailable(),
  recallMemories: () => unavailable(),
};

export const emptyQuery: QuerySource = {
  kind: 'empty',
  totals: () => unavailable(),
  throughput: () => unavailable(),
  tokensByModel: () => unavailable(),
  usageByTenant: () => unavailable(),
  providerHealth: () => unavailable(),
  realmMix: () => unavailable(),
  reconciliation: () => unavailable(),
};

export const emptySources: Sources = {
  live: emptyLive,
  query: emptyQuery,
  canMutate: false,
};
